import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useUnknownCards } from '../../api/drivers';
import { usePermission } from '../../auth/permissions';
import { formatAgo } from '../../lib/format';
import { colours, radius } from '../../theme';

/**
 * Cards tapped on your chargers in the last two weeks that nobody has registered. Ask the
 * driver to tap theirs on a charger, then pick it here instead of typing its number.
 */
export function UnknownCards({ organisationId, onPick, exclude = [] }: { organisationId?: string; onPick(tag: string): void; exclude?: string[] }) {
  const canEdit = usePermission('cards.edit');
  const { data } = useUnknownCards(organisationId, canEdit);
  const skip = new Set(exclude.map((t) => t.toUpperCase()));
  const rows = (data ?? []).filter((c) => !skip.has(c.tag.toUpperCase())).slice(0, 5);
  if (!rows.length) return null;
  return (
    <Box sx={{ border: `1px solid ${colours.hairline}`, borderRadius: `${radius.panel}px`, p: 2 }}>
      <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Tapped recently, not registered</Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
        Ask the driver to tap their card on a charger, then pick it here.
      </Typography>
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1 }}>
        {rows.map((c) => (
          <Box component="li" key={c.tag} sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography component="span" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 15 }}>{c.tag}</Typography>
              <Typography variant="body2" color="textSecondary">
                {[c.charger, c.siteName].filter(Boolean).join(', ')} · {formatAgo(c.lastSeenAt)}
                {c.times > 1 ? ` · ${c.times} taps` : ''}
              </Typography>
            </Box>
            <Button size="small" onClick={() => onPick(c.tag)} aria-label={`Use card ${c.tag}`}>
              Use this card
            </Button>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
